import React, { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import { supabase } from "../lib/supabaseClient";
import { usePlatformSecurityCapabilities } from "../context/PlatformSecurityCapabilityContext.jsx";

function money(minor, currency = "USD") {
  const amount = Number(minor || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: String(currency || "USD").toUpperCase(),
    }).format(amount);
  } catch {
    return `${String(currency || "USD").toUpperCase()} ${amount.toFixed(2)}`;
  }
}

function dateOnly(value) {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(`${value}T12:00:00`));
  } catch {
    return String(value);
  }
}

function dateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

function accountLabel(value) {
  const raw = String(value || "");
  if (raw.length <= 14) return raw || "—";
  return `${raw.slice(0, 7)}…${raw.slice(-6)}`;
}

function correlation(prefix) {
  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${id}`;
}

function StatusPill({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    red: "border-red-200 bg-red-50 text-red-800",
    blue: "border-blue-200 bg-blue-50 text-blue-800",
  };
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${
        tones[tone] || tones.slate
      }`}
    >
      {children}
    </span>
  );
}

export default function SalesAffiliatePayoutAdmin() {
  const { status: capabilityStatus, hasCapability } =
    usePlatformSecurityCapabilities();

  const canManagePayouts =
    capabilityStatus === "ready" && hasCapability("MANAGE_PAYOUTS");

  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [payload, setPayload] = useState({
    items: [],
    payout_execution_enabled: false,
    stripe_expected_livemode: false,
    reporting_as_of_date_et: null,
  });

  const load = useCallback(async () => {
    if (!canManagePayouts) return;
    setLoading(true);
    setError("");
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "sales_affiliate_commission_payout",
        { body: { action: "list" } }
      );

      if (invokeError) throw invokeError;
      if (!data?.ok) {
        throw new Error(data?.error || "Unable to load commission payouts.");
      }

      setPayload({
        items: Array.isArray(data.items) ? data.items : [],
        payout_execution_enabled: data.payout_execution_enabled === true,
        stripe_expected_livemode: data.stripe_expected_livemode === true,
        reporting_as_of_date_et: data.reporting_as_of_date_et || null,
      });
    } catch (err) {
      setError(String(err?.message || err || "Unable to load commission payouts."));
    } finally {
      setLoading(false);
    }
  }, [canManagePayouts]);

  useEffect(() => {
    if (canManagePayouts) load();
  }, [canManagePayouts, load]);

  const summary = useMemo(() => {
    const rows = payload.items || [];
    return {
      total: rows.length,
      due: rows.filter((x) => x.is_due).length,
      approved: rows.filter((x) => x.lifecycle_status === "APPROVED").length,
      ready: rows.filter(
        (x) =>
          x.is_due &&
          x.lifecycle_status === "APPROVED" &&
          ["VERIFIED", "COMPLETE"].includes(x.payment_status) &&
          Number(x.net_payable_minor || 0) > 0
      ).length,
    };
  }, [payload.items]);

  async function approve(item) {
    const amount = money(item.net_payable_minor, item.currency);
    const ok = window.confirm(
      `Approve this Sales Affiliate commission for payout?\n\nAffiliate: ${
        item.affiliate_name || item.affiliate_email || item.affiliate_id
      }\nAmount currently payable: ${amount}\nScheduled date: ${item.scheduled_payment_date}\n\nApproval does not move money.`
    );
    if (!ok) return;

    setBusyId(item.commission_entry_id);
    setError("");
    setNotice("");
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "sales_affiliate_commission_payout",
        {
          body: {
            action: "approve",
            commission_entry_id: item.commission_entry_id,
            correlation_id: correlation("sa-payout-approve"),
          },
        }
      );
      if (invokeError) throw invokeError;
      if (!data?.ok) throw new Error(data?.error || "Approval failed.");

      setNotice(
        `Commission approved. Current payable amount: ${money(
          data.net_payable_minor,
          data.currency
        )}. No money was moved.`
      );
      await load();
    } catch (err) {
      setError(String(err?.message || err || "Approval failed."));
    } finally {
      setBusyId("");
    }
  }

  async function pay(item) {
    const live = payload.stripe_expected_livemode === true;
    const mode = live ? "LIVE" : "TEST";
    const amountMinor = Number(item.net_payable_minor || 0);
    const amount = money(amountMinor, item.currency);

    const warning = live
      ? `LIVE STRIPE MONEY MOVEMENT\n\nThis will attempt to transfer ${amount} from the AFP Stripe platform balance to the Sales Affiliate connected account.`
      : `STRIPE TEST MODE\n\nThis will create/recover a test transfer for ${amount}. No live money should move.`;

    const ok = window.confirm(
      `${warning}\n\nAffiliate: ${
        item.affiliate_name || item.affiliate_email || item.affiliate_id
      }\nDestination: ${item.provider_account_ref}\nCommission: ${
        item.commission_entry_id
      }\n\nContinue?`
    );
    if (!ok) return;

    setBusyId(item.commission_entry_id);
    setError("");
    setNotice("");
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "sales_affiliate_commission_payout",
        {
          body: {
            action: "pay",
            commission_entry_id: item.commission_entry_id,
            confirm_commission_entry_id: item.commission_entry_id,
            confirm_amount_minor: amountMinor,
            confirm_provider_account_ref: item.provider_account_ref,
            confirm_mode: mode,
            correlation_id: correlation("sa-payout-pay"),
          },
        }
      );

      if (invokeError) throw invokeError;
      if (!data?.ok) {
        if (data?.error === "payout_confirmation_stale_refresh_required") {
          throw new Error(
            "The authoritative payout amount or destination changed. Refresh and review before paying."
          );
        }
        throw new Error(data?.error || "Payout failed.");
      }

      setNotice(
        `${mode} payout recorded successfully: ${money(
          data.amount_minor,
          data.currency
        )} · Stripe transfer ${data.provider_transfer_reference || "recorded"}.`
      );
      await load();
    } catch (err) {
      setError(String(err?.message || err || "Payout failed."));
    } finally {
      setBusyId("");
    }
  }

  if (capabilityStatus === "idle" || capabilityStatus === "loading") {
    return (
      <div className="min-h-screen flex bg-slate-50">
        <Sidebar />
        <main className="flex-1 p-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm font-semibold">
            Checking payout authority…
          </div>
        </main>
      </div>
    );
  }

  if (!canManagePayouts) {
    return (
      <div className="min-h-screen flex bg-slate-50">
        <Sidebar />
        <main className="flex-1 p-8">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-800">
            This page requires the MANAGE_PAYOUTS capability.
          </div>
        </main>
      </div>
    );
  }

  const live = payload.stripe_expected_livemode === true;

  return (
    <div className="min-h-screen flex bg-slate-50">
      <Sidebar />

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-7 py-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.35em] text-blue-600">
                Commercial Operations
              </div>
              <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
                Sales Affiliate Commission Payouts
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Controlled Review → Approve → Pay workflow. Approval never moves
                money. Pay uses the certified commission ledger, verified Stripe
                Connect destination, durable idempotency, and authoritative payment
                evidence.
              </p>
            </div>

            <button
              type="button"
              onClick={load}
              disabled={loading || Boolean(busyId)}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-black text-slate-800 hover:bg-slate-100 disabled:opacity-50"
            >
              Refresh
            </button>
          </div>

          <div
            className={`mt-6 rounded-2xl border p-4 ${
              live
                ? "border-red-300 bg-red-50 text-red-900"
                : "border-blue-200 bg-blue-50 text-blue-900"
            }`}
          >
            <div className="flex flex-wrap items-center gap-3">
              <StatusPill tone={live ? "red" : "blue"}>
                {live ? "LIVE STRIPE MODE" : "STRIPE TEST MODE"}
              </StatusPill>
              <StatusPill
                tone={payload.payout_execution_enabled ? "green" : "amber"}
              >
                {payload.payout_execution_enabled
                  ? "Execution enabled"
                  : "Execution disabled"}
              </StatusPill>
            </div>
            <div className="mt-2 text-sm font-semibold">
              {live
                ? "Pay can move real money when execution is enabled. Review the affiliate, destination account, amount, and scheduled date before confirming."
                : "This environment is expected to use Stripe test/sandbox money only."}
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-4">
            {[
              ["Visible", summary.total],
              ["Due", summary.due],
              ["Approved", summary.approved],
              ["Ready to pay", summary.ready],
            ].map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-slate-200 bg-white p-4"
              >
                <div className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
                  {label}
                </div>
                <div className="mt-2 text-2xl font-black text-slate-950">
                  {value}
                </div>
              </div>
            ))}
          </div>

          {notice ? (
            <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-900">
              {notice}
            </div>
          ) : null}

          {error ? (
            <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-900">
              {error}
            </div>
          ) : null}

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-5 py-4">
              <div className="font-black text-slate-950">
                Due and upcoming commissions
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Shows unpaid commissions scheduled through the next 31 days.
                Reporting date (Eastern):{" "}
                {payload.reporting_as_of_date_et || "—"}.
              </div>
            </div>

            {loading ? (
              <div className="p-6 text-sm text-slate-500">Loading payouts…</div>
            ) : payload.items.length === 0 ? (
              <div className="p-6 text-sm text-slate-500">
                No unpaid commissions are currently due or scheduled in the next
                31 days.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {payload.items.map((item) => {
                  const verified = ["VERIFIED", "COMPLETE"].includes(
                    item.payment_status
                  );
                  const due = item.is_due === true;
                  const approved = item.lifecycle_status === "APPROVED";
                  const positive = Number(item.net_payable_minor || 0) > 0;
                  const working = busyId === item.commission_entry_id;
                  const canApprove =
                    !working &&
                    due &&
                    !approved &&
                    item.lifecycle_status === "HELD" &&
                    verified &&
                    positive;
                  const canPay =
                    !working &&
                    due &&
                    approved &&
                    verified &&
                    positive &&
                    Boolean(item.provider_account_ref) &&
                    payload.payout_execution_enabled;

                  return (
                    <div key={item.commission_entry_id} className="p-5">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <div className="text-lg font-black text-slate-950">
                            {item.affiliate_name ||
                              item.affiliate_email ||
                              "Sales Affiliate"}
                          </div>
                          <div className="mt-1 text-sm text-slate-600">
                            {item.affiliate_email || "—"}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <StatusPill tone={due ? "green" : "amber"}>
                              {due ? "Due" : "Upcoming"}
                            </StatusPill>
                            <StatusPill
                              tone={approved ? "blue" : "slate"}
                            >
                              {item.lifecycle_status}
                            </StatusPill>
                            <StatusPill tone={verified ? "green" : "red"}>
                              Payment {item.payment_status || "NOT READY"}
                            </StatusPill>
                            {item.disbursement_state ? (
                              <StatusPill tone="amber">
                                {item.disbursement_state}
                              </StatusPill>
                            ) : null}
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
                            Net payable now
                          </div>
                          <div className="mt-1 text-2xl font-black text-slate-950">
                            {money(item.net_payable_minor, item.currency)}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            Base {money(item.commission_amount_minor, item.currency)}
                            {" · "}Adjustments{" "}
                            {money(item.adjustments_minor, item.currency)}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 text-xs text-slate-600 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                          <div className="font-black uppercase tracking-[0.18em] text-slate-400">
                            Scheduled
                          </div>
                          <div className="mt-1 font-semibold text-slate-800">
                            {dateOnly(item.scheduled_payment_date)}
                          </div>
                        </div>
                        <div>
                          <div className="font-black uppercase tracking-[0.18em] text-slate-400">
                            Hold until
                          </div>
                          <div className="mt-1 font-semibold text-slate-800">
                            {dateTime(item.hold_until)}
                          </div>
                        </div>
                        <div>
                          <div className="font-black uppercase tracking-[0.18em] text-slate-400">
                            Stripe destination
                          </div>
                          <div
                            className="mt-1 font-mono font-semibold text-slate-800"
                            title={item.provider_account_ref || ""}
                          >
                            {accountLabel(item.provider_account_ref)}
                          </div>
                        </div>
                        <div>
                          <div className="font-black uppercase tracking-[0.18em] text-slate-400">
                            Commission
                          </div>
                          <div className="mt-1 font-mono font-semibold text-slate-800">
                            {String(item.commission_entry_id).slice(0, 8)}…
                          </div>
                        </div>
                      </div>

                      {item.last_provider_error_code ? (
                        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
                          Last provider attempt: {item.last_provider_error_code}
                          {item.last_provider_error_at
                            ? ` · ${dateTime(item.last_provider_error_at)}`
                            : ""}
                        </div>
                      ) : null}

                      <div className="mt-4 flex flex-wrap items-center gap-3">
                        {item.lifecycle_status === "HELD" ? (
                          <button
                            type="button"
                            onClick={() => approve(item)}
                            disabled={!canApprove}
                            className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-black text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {working ? "Working…" : "Approve commission"}
                          </button>
                        ) : null}

                        {approved ? (
                          <button
                            type="button"
                            onClick={() => pay(item)}
                            disabled={!canPay}
                            className={`rounded-xl px-4 py-2 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-40 ${
                              live
                                ? "bg-red-700 hover:bg-red-800"
                                : "bg-blue-700 hover:bg-blue-800"
                            }`}
                          >
                            {working
                              ? "Working…"
                              : live
                              ? "Pay via Stripe — LIVE"
                              : "Pay via Stripe — TEST"}
                          </button>
                        ) : null}

                        {!due ? (
                          <span className="text-xs font-semibold text-slate-500">
                            Not payable before the scheduled payment date.
                          </span>
                        ) : null}
                        {!verified ? (
                          <span className="text-xs font-semibold text-red-700">
                            Stripe payment setup must be verified before approval
                            or payment.
                          </span>
                        ) : null}
                        {approved && !payload.payout_execution_enabled ? (
                          <span className="text-xs font-semibold text-amber-700">
                            Payout execution kill switch is disabled.
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
